import {Logger, LoggerOptions, pino as createLogger} from "pino";

export type {Logger, LoggerOptions} from "pino";
export {pino as createLogger} from "pino";

export const baseLoggerConfig: LoggerOptions = {
    errorKey: "error", // not err
    messageKey: "message",

    hooks: {
        /**
         * We allow support for console.log and Winston-style logger.info(message, objectContext) logging
         *
         * By default, pino only works for logger.info(objectContext, message) logging. There are subtleties here, like
         * if the call is logger.info("some %o", {foo: "bar"}), then we don't want to reverse those two arguments (it
         * would be unwanted for {foo: "bar"} to end up in the structured log; they should be formatted into the message
         * string).
         */
        logMethod(inputArgs, method, _level) {
            const [arg0, arg1, ...rest] = inputArgs;

            if (arg0 && typeof arg0 === "string" && !arg0.includes("%") && arg1 && typeof arg1 === "object") {
                // @ts-expect-error -- arg1 is the merging object, not a string, but method.apply is picking the wrong overload or something
                return method.apply(this, [arg1, arg0, ...rest]);
            }

            return method.apply(this, inputArgs);
        },
    },
};

const levelToSeverity: Record<string, string> = {
    trace: "DEBUG",
    debug: "DEBUG",
    info: "INFO",
    warn: "WARNING",
    error: "ERROR",
    fatal: "CRITICAL",
};

/**
 * Logger config for services running on GCP (Cloud Run, Cloud Functions).
 *
 * Cloud Logging only understands a log line's level when the JSON payload
 * carries a `severity` field; pino's numeric `level` is ignored, so every line
 * lands as severity DEFAULT — which sorts below INFO and is therefore dropped
 * by severity-filtered log sinks. Mapping
 * the level to `severity` is what makes the logs visible, and lifting the
 * error stack to `stack_trace` is what makes Error Reporting group them.
 */
export const gcpStructuredLoggerConfig: LoggerOptions = {
    ...baseLoggerConfig,
    level: "info",
    base: undefined,
    formatters: {
        level(label: string) {
            return {severity: levelToSeverity[label] ?? "INFO"};
        },
        log(object) {
            const logObject = object as {err?: Error; error?: unknown};
            const stackTrace =
                logObject.err?.stack ?? (logObject.error instanceof Error ? logObject.error.stack : undefined);
            return stackTrace ? {...object, stack_trace: stackTrace} : object;
        },
    },
    timestamp: createLogger.stdTimeFunctions.isoTime,
};

export const silentLogger: Logger = createLogger({...baseLoggerConfig, level: "silent"});

/**
 * This global logger exists to help us migrate to using structured logging instead of console.log()
 *
 * One great convenience of `console` is that it is a global singleton, and that you don't need to worry about
 * passing it around. This is bad for testability, but good for ease of use. This global logger is similarly
 * very easy to access, but the downside is the application needs to replace this global singleton with a
 * properly configured logger instance, or anything using this will log incorrectly - bad formatting etc.
 *
 * Definitely try not to add additional uses of this export. You should be able to access a logger from
 * your context (e.g. dependency injection, or passed as a parameter), and optionally add bindings to it with
 * .child()
 *
 * @deprecated Get a logger from your parent context instead of using this global singleton
 */
export let globalFallbackLogger: Logger = createLogger({level: "info"});

/**
 * Sets the global fallback logger
 * @param logger
 */
export function setGlobalFallbackLogger(logger: Logger): void {
    globalFallbackLogger = logger;
}

declare module "pino" {
    /**
     * This is using declaration merging and adding two additional call patterns.
     *
     * The ones that are already supported are:
     *
     *  - (message: string, ...argsForMessage: ParseLogFnArgs): void;
     *    - i.e. where you only provide a string message and sprintf formatting args
     *  - (mergingObject: T, message?: string, ...argsForMessage: ParseLogFnArgs): void;
     *    - i.e. where you provide a merging object first, and optionally a message and sprintf formatting args
     *
     * So, what's lacking is something to account for calls like:
     *
     *    logger.info("Some debug message", {some: "context"});
     *
     * With just the above types, this call would be invalid (unless "%o" was added to the debug message, and even then
     * it _wouldn't_ be logging the additional "some" property into structured loggingg (bad)). We want to treat the
     * second argument as the merging object.
     *
     * Therefore, in the logMethod hook of the baseLoggerConfig, we swap the order of the message and merging object.
     * To make the types reflect that support, we mostly copy the upstream LogFn interface, but with the message and
     * merging object swapped on the calls.
     *
     * @see import("pino").LogFn
     */
    interface LogFn {
        // Complex case: When first argument can be any type - if it's a string, no message needed; otherwise require a message
        <T, TMsg extends string = string>(
            message: T extends string ? never : TMsg,
            mergingObject: T extends object ? T & import("pino").LogFnFields : T,
            ...argsForMessage: import("pino").ParseLogFnArgs<TMsg> | []
        ): void;

        // Complex case with type safety: Same as above but ensures ParseLogFnArgs is a valid tuple before using it
        <T, TMsg extends string = string>(
            message: T extends string ? never : TMsg,
            mergingObject: T extends object ? T & import("pino").LogFnFields : T,
            ...argsForMessage: import("pino").ParseLogFnArgs<TMsg> extends [unknown, ...unknown[]]
                ? import("pino").ParseLogFnArgs<TMsg>
                : unknown[]
        ): void;
    }
}
